import { createSlice } from "@reduxjs/toolkit";
import {
  iniciarConversacionThunk,
  getSesionActualThunk,
  getConfiguracionesThunk,
  responderThunk,
  responderConMediaThunk,
  adjuntarMediaThunk,
  eliminarMediaThunk,
  completarSesionThunk,
  getMisSesionesThunk,
  getResumenThunk,
} from "./conversacion.thunks";

/* ── Helper ── */
const unwrap = (payload) => payload?.data ?? payload;

const initialState = {
  // Sesión activa
  sesionActiva: null,
  sesionActivaLoading: false,

  // Configuraciones + respuestas de la sesión
  configuraciones: [],
  respuestas: [],
  configuracionesLoading: false,

  // Mis sesiones (historial)
  misSesiones: [],
  misSesionesLoading: false,

  // Resumen detallado
  resumen: null,
  resumenLoading: false,

  // Estados de carga para acciones
  iniciando: false,
  respondiendo: false,
  adjuntando: false,
  completando: false,

  // Error general
  error: null,
};

const conversacionSlice = createSlice({
  name: "conversacion",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetConversacion: () => initialState,
  },
  extraReducers: (builder) => {
    // ── Iniciar sesión ──────────────────────────
    builder
      .addCase(iniciarConversacionThunk.pending, (state) => {
        state.iniciando = true;
        state.error = null;
      })
      .addCase(iniciarConversacionThunk.fulfilled, (state, action) => {
        state.iniciando = false;
        const d = unwrap(action.payload);
        if (d) {
          state.sesionActiva = d;
        }
      })
      .addCase(iniciarConversacionThunk.rejected, (state, action) => {
        state.iniciando = false;
        state.error = action.payload;
      });

    // ── Sesión actual ───────────────────────────
    builder
      .addCase(getSesionActualThunk.pending, (state) => {
        state.sesionActivaLoading = true;
        state.error = null;
      })
      .addCase(getSesionActualThunk.fulfilled, (state, action) => {
        state.sesionActivaLoading = false;
        const d = unwrap(action.payload);
        state.sesionActiva = d;
      })
      .addCase(getSesionActualThunk.rejected, (state, action) => {
        state.sesionActivaLoading = false;
        // 404 = no hay sesión activa, no es error crítico
        if (action.payload !== "No tienes ninguna sesión de conversación activa.") {
          state.error = action.payload;
        }
        state.sesionActiva = null;
      });

    // ── Configuraciones ─────────────────────────
    builder
      .addCase(getConfiguracionesThunk.pending, (state) => {
        state.configuracionesLoading = true;
        state.error = null;
      })
      .addCase(getConfiguracionesThunk.fulfilled, (state, action) => {
        state.configuracionesLoading = false;
        const d = unwrap(action.payload);
        if (d) {
          state.configuraciones = d.configuraciones || [];
          state.respuestas = d.respuestas || [];
        }
      })
      .addCase(getConfiguracionesThunk.rejected, (state, action) => {
        state.configuracionesLoading = false;
        state.error = action.payload;
      });

    // ── Responder (texto) ───────────────────────
    builder
      .addCase(responderThunk.pending, (state) => {
        state.respondiendo = true;
        state.error = null;
      })
      .addCase(responderThunk.fulfilled, (state, action) => {
        state.respondiendo = false;
        // Actualizar la respuesta en el estado local
        const d = unwrap(action.payload);
        if (d) {
          const idx = state.respuestas.findIndex(
            (r) =>
              r.configuracion_conversacion === d.configuracion_conversacion &&
              r.tipo_autor === d.tipo_autor,
          );
          if (idx >= 0) {
            state.respuestas[idx] = d;
          } else {
            state.respuestas.push(d);
          }
        }
      })
      .addCase(responderThunk.rejected, (state, action) => {
        state.respondiendo = false;
        state.error = action.payload;
      });

    // ── Responder con media ─────────────────────
    builder
      .addCase(responderConMediaThunk.pending, (state) => {
        state.respondiendo = true;
        state.error = null;
      })
      .addCase(responderConMediaThunk.fulfilled, (state, action) => {
        state.respondiendo = false;
        const d = unwrap(action.payload);
        if (d) {
          const idx = state.respuestas.findIndex(
            (r) =>
              r.configuracion_conversacion === d.configuracion_conversacion &&
              r.tipo_autor === d.tipo_autor,
          );
          if (idx >= 0) {
            state.respuestas[idx] = d;
          } else {
            state.respuestas.push(d);
          }
        }
      })
      .addCase(responderConMediaThunk.rejected, (state, action) => {
        state.respondiendo = false;
        state.error = action.payload;
      });

    // ── Adjuntar media ──────────────────────────
    builder
      .addCase(adjuntarMediaThunk.pending, (state) => {
        state.adjuntando = true;
        state.error = null;
      })
      .addCase(adjuntarMediaThunk.fulfilled, (state) => {
        state.adjuntando = false;
      })
      .addCase(adjuntarMediaThunk.rejected, (state, action) => {
        state.adjuntando = false;
        state.error = action.payload;
      });

    // ── Eliminar media ──────────────────────────
    builder
      .addCase(eliminarMediaThunk.fulfilled, (state, action) => {
        const mediaId = action.payload?.mediaId;
        if (mediaId) {
          // Remover el archivo del estado local
          state.respuestas = state.respuestas.map((r) => ({
            ...r,
            archivos_multimedia: (r.archivos_multimedia || []).filter(
              (m) => m.id !== mediaId,
            ),
          }));
        }
      })
      .addCase(eliminarMediaThunk.rejected, (state, action) => {
        state.error = action.payload;
      });

    // ── Completar sesión ────────────────────────
    builder
      .addCase(completarSesionThunk.pending, (state) => {
        state.completando = true;
        state.error = null;
      })
      .addCase(completarSesionThunk.fulfilled, (state, action) => {
        state.completando = false;
        const d = unwrap(action.payload);
        if (d) {
          state.resumen = d;
          if (state.sesionActiva) {
            state.sesionActiva.estado = "completado";
            state.sesionActiva.estado_display = "Completado";
          }
        }
      })
      .addCase(completarSesionThunk.rejected, (state, action) => {
        state.completando = false;
        state.error = action.payload;
      });

    // ── Mis sesiones ────────────────────────────
    builder
      .addCase(getMisSesionesThunk.pending, (state) => {
        state.misSesionesLoading = true;
        state.error = null;
      })
      .addCase(getMisSesionesThunk.fulfilled, (state, action) => {
        state.misSesionesLoading = false;
        const d = unwrap(action.payload);
        state.misSesiones = Array.isArray(d) ? d : [];
      })
      .addCase(getMisSesionesThunk.rejected, (state, action) => {
        state.misSesionesLoading = false;
        state.error = action.payload;
      });

    // ── Resumen ─────────────────────────────────
    builder
      .addCase(getResumenThunk.pending, (state) => {
        state.resumenLoading = true;
        state.error = null;
      })
      .addCase(getResumenThunk.fulfilled, (state, action) => {
        state.resumenLoading = false;
        const d = unwrap(action.payload);
        state.resumen = d;
      })
      .addCase(getResumenThunk.rejected, (state, action) => {
        state.resumenLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetConversacion } = conversacionSlice.actions;
export default conversacionSlice.reducer;
